import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * A page that fails to draw fails alone (docs/07-Admin/Admin-App.md).
 *
 * Without this, one render error anywhere blanked the whole admin — menu and
 * all — and the only way back was a reload nobody was told to try. The shell
 * keeps standing, the page says what happened and how to get back, and moving
 * to another page clears it (`resetKey`).
 *
 * The words come from the caller: a class component cannot use the
 * translator hook.
 */
export class PageErrorBoundary extends Component<
  { resetKey: string; fallback: (reload: () => void) => ReactNode; children: ReactNode },
  { failed: boolean; key: string }
> {
  state = { failed: false, key: this.props.resetKey };

  static getDerivedStateFromError(): Partial<{ failed: boolean }> {
    return { failed: true };
  }

  static getDerivedStateFromProps(
    props: { resetKey: string },
    state: { failed: boolean; key: string },
  ): Partial<{ failed: boolean; key: string }> | null {
    // A new page is a fresh start.
    return props.resetKey !== state.key ? { failed: false, key: props.resetKey } : null;
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('A page failed to render', error, info.componentStack);
  }

  render(): ReactNode {
    return this.state.failed ? this.props.fallback(() => window.location.reload()) : this.props.children;
  }
}
